import _ from 'lodash'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { AudioMixObserver, AudioVideoObserver, MeetingSessionConfiguration } from 'amazon-chime-sdk-js'
import {
  ContentShare,
  DeviceLabels,
  LocalVideo,
  MeetingManagerJoinOptions,
  RemoteVideos,
  useAudioVideo,
  useContentShareControls,
  useLocalVideo,
  useMeetingManager,
  VideoGrid,
} from 'amazon-chime-sdk-component-library-react'
import { dataURLToBlob } from 'blob-util'

import { Box, Stack } from '@mui/material'
import { styled } from '@mui/material/styles'

import useMediaDevices from '@/hooks/useMediaDevices'
import { getMedicalCameras, getStethoscopes, getUserMedia } from '@/utils/devices'

import AudioGainControl from '../components/AudioGainControl'
import LiveAudioVisualizer from '../components/LiveAudioVisualizer'
import { DiagnosticRTCServiceHandle, DiagnosticRTCServiceProps } from '../types'
import { useDiagnosticRTC } from '../DiagnosticRTCProvider'
import { microphonePluginConstraintsGenerator, normalizeDevice, selectDeviceWithRetry } from '../utils'
import { createAudioProcessingChain, getFirstAudioTrackLabel, normalizeLabel } from '../components/utils'

const StyledLocalVideo = styled(LocalVideo)({
  transform: 'scaleX(-1)',
  position: 'relative',
  '& video': {
    objectFit: 'contain !important',
    width: '100% !important',
    height: '100% !important',
    maxWidth: '100% !important',
    maxHeight: '100% !important',
    // Fallback for browsers that don't support object-fit
    objectPosition: 'center center !important',
    // Ensure video maintains aspect ratio
    aspectRatio: '16/9',
    // Prevent overflow
    overflow: 'hidden !important',
    // Additional properties for better compatibility
    display: 'block !important',
    // Support for older browsers
    '@supports not (object-fit: contain)': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      minWidth: '100%',
      minHeight: '100%',
    },
  },
  // Ensure the container itself is properly sized
  '& > div': {
    width: '100% !important',
    height: '100% !important',
    position: 'relative !important',
    overflow: 'hidden !important',
  },
})

const StyledRemoteVideos = styled(RemoteVideos)({
  position: 'relative',
  '& video': {
    objectFit: 'contain !important',
    width: '100% !important',
    height: '100% !important',
    maxWidth: '100% !important',
    maxHeight: '100% !important',
    // Fallback for browsers that don't support object-fit
    objectPosition: 'center center !important',
    // Ensure video maintains aspect ratio
    aspectRatio: '16/9',
    // Prevent overflow
    overflow: 'hidden !important',
    // Additional properties for better compatibility
    display: 'block !important',
    // Support for older browsers
    '@supports not (object-fit: contain)': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      minWidth: '100%',
      minHeight: '100%',
    },
  },
  // Ensure the container itself is properly sized
  '& > div': {
    width: '100% !important',
    height: '100% !important',
    position: 'relative !important',
    overflow: 'hidden !important',
  },
  // Target the actual video container more specifically
  '& .ch-video-wrapper, & .ch-video-list, & .ch-video-grid': {
    '& video': {
      objectFit: 'contain !important',
      width: '100% !important',
      height: '100% !important',
      maxWidth: '100% !important',
      maxHeight: '100% !important',
      // Support for older browsers
      '@supports not (object-fit: contain)': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: '100%',
        minHeight: '100%',
      },
    },
  },
})

type ChimeProps = DiagnosticRTCServiceProps & {
  attendeeInfo: Record<string, any> | null
  children: React.ReactNode
  meetingInfo: Record<string, any> | null
}

function Chime(props: ChimeProps, ref: React.Ref<DiagnosticRTCServiceHandle>) {
  const { attendeeInfo, children, customDevices, meetingInfo, onDevicesChange, onStreamChange, type } = props
  const audioVideo = useAudioVideo()
  const meetingManager = useMeetingManager()
  const { toggleContentShare } = useContentShareControls()
  const { activeDiagnosticRTCService, isStreamer } = useDiagnosticRTC()
  const { setIsVideoEnabled } = useLocalVideo()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const mediaDevices = useMediaDevices()

  const audioRef = useRef<HTMLAudioElement>(null)
  const audioContextRef = useRef<AudioContext>(new AudioContext())
  const gainNodeRef = useRef<GainNode>(audioContextRef.current.createGain()) // For output gain control

  const [selectedDeviceId, setSelectedDeviceId] = useState<string>()
  const [stream, setStream] = useState<MediaStream | null>(null)

  const { medicalCameras, videoMediaDevices } = useMemo(() => {
    const medicalCameras = _.map(getMedicalCameras(customDevices, mediaDevices), (medicalCameraDevice) => ({
      id: medicalCameraDevice._id,
      device: {
        deviceId: medicalCameraDevice.device.deviceId,
        label: medicalCameraDevice.device.name,
        kind: medicalCameraDevice.device.type,
      },
      customName: medicalCameraDevice.customName,
      iconURL: medicalCameraDevice.iconURL,
      type: medicalCameraDevice.type,
    }))

    const videoMediaDevices = _.filter(mediaDevices, (device) => device.kind === 'videoinput')

    return { medicalCameras, videoMediaDevices }
  }, [customDevices, mediaDevices])

  const { audioInputMediaDevices, stethoscopes } = useMemo(() => {
    const stethoscopes = _.map(getStethoscopes(customDevices, mediaDevices), (stethoscopeDevice) => ({
      id: stethoscopeDevice._id,
      device: {
        deviceId: stethoscopeDevice.device.deviceId,
        label: stethoscopeDevice.device.name,
        kind: stethoscopeDevice.device.type,
      },
      customName: stethoscopeDevice.customName,
      iconURL: stethoscopeDevice.iconURL,
      type: stethoscopeDevice.type,
    }))

    const audioInputMediaDevices = _.filter(mediaDevices, (device) => device.kind === 'audioinput')

    return { audioInputMediaDevices, stethoscopes }
  }, [customDevices, mediaDevices])

  useImperativeHandle(ref, () => ({
    captureScreen,
    getMedicalCameras,
    getStream: () => stream,
    renderAudio,
    renderAudioVisualizer,
    renderVideo,
    selectDevice,
    shareScreen,
    stopMicro,
    stopVideo,
  }))

  const captureScreen = useCallback(
    (cb: (imageFile: File) => void) => {
      // Attempt to find a content share tile
      const videoTiles = audioVideo?.getAllVideoTiles()
      let targetTile =
        videoTiles?.find((tile) => tile.state().isContent) ?? (isStreamer ? audioVideo?.getLocalVideoTile() : audioVideo?.getAllRemoteVideoTiles()[0])

      if (!targetTile) return // Exit if no suitable tile is found

      const imageData = targetTile.capture()

      if (!imageData) return // Exit if unable to capture image data

      // Proceed to create and return the screenshot as before
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = imageData.width
      canvas.height = imageData.height
      ctx?.putImageData(imageData, 0, 0)

      const imgDataUrl = canvas.toDataURL('image/png')
      const blob = dataURLToBlob(imgDataUrl)
      const imgName = `medical-screenshot-${Date.now().toString()}.png`
      const file = new File([blob], imgName, { type: 'image/png' })
      cb(file)
    },
    [audioVideo, isStreamer]
  )

  const renderAudio = useCallback(() => (isStreamer ? <></> : <></>), [isStreamer])

  const renderAudioVisualizer = useCallback(() => {
    return (
      <>
        <Stack direction="row" spacing={1} p={1}>
          <AudioGainControl
            initialGain={isStreamer ? 1 : 5}
            onGainChange={(gain) => {
              if (!gainNodeRef.current) return
              gainNodeRef.current.gain.value = gain
            }}
          />
        </Stack>
        <LiveAudioVisualizer stream={stream ?? undefined} />
      </>
    )
  }, [stream, isStreamer])

  const renderVideo = useCallback(() => (audioVideo?.hasStartedLocalVideoTile() ? <VideoRenderer isStreamer={isStreamer} /> : <></>), [audioVideo, isStreamer])

  const selectDevice = useCallback(
    (deviceId: string) => {
      // Log warning about selected device
      const selectedDevice = [...stethoscopes, ...audioInputMediaDevices, ...medicalCameras, ...videoMediaDevices]
        .map(normalizeDevice)
        .find((device) => device.deviceId === deviceId)
      console.warn(`🎯 DEVICE SELECTION REQUESTED:`, {
        requestedDeviceId: deviceId,
        requestedDeviceName: selectedDevice?.label || 'Unknown',
        deviceType: type,
        isStethoscope: selectedDevice?.type === 'stethoscope',
        isMedicalCamera: selectedDevice?.type === 'medicalCamera',
      })

      setSelectedDeviceId(deviceId)
    },
    [type, stethoscopes, audioInputMediaDevices, medicalCameras, videoMediaDevices]
  )

  const shareScreen = useCallback(() => {
    toggleContentShare()
  }, [toggleContentShare])

  const stopMicro = useCallback(() => {
    meetingManager.startAudioInputDevice(null).then(() => {
      setStream(null)
      setSelectedDeviceId(undefined)
    })
  }, [meetingManager])

  const stopVideo = useCallback(() => {
    meetingManager.stopVideoInputDevice().then(() => {
      setSelectedDeviceId(undefined)
    })
  }, [meetingManager])

  useEffect(() => {
    const audioContext = audioContextRef.current
    // Resume AudioContext if suspended (browser autoplay policy).
    // User gesture from tab click propagates here, allowing resume.
    if (audioContext.state === 'suspended') {
      audioContext.resume().then(() => {
        console.warn(`🔊 CHIME AudioContext resumed on mount — state: ${audioContext.state}`)
      })
    } else {
      console.warn(`🔊 CHIME AudioContext already running on mount — state: ${audioContext.state}`)
    }
    return () => {
      audioContext.close()
    }
  }, [])

  useEffect(() => {
    const audioElement = audioRef.current
    if (!audioElement || !isStreamer || !stream || type !== 'audio') return
    audioElement.srcObject = stream
    audioElement.onloadedmetadata = () => {
      audioElement.play()
    }

    return () => {
      stream.getAudioTracks().forEach((track) => {
        track.stop()
      })
    }
  }, [isStreamer, stream, type])

  useEffect(() => {
    const startMeeting = async (meetingInfo: any, attendeeInfo: any) => {
      if (!meetingInfo || !attendeeInfo) return

      const meetingSessionConfiguration = new MeetingSessionConfiguration(meetingInfo, attendeeInfo)
      const options: MeetingManagerJoinOptions = {
        deviceLabels: DeviceLabels.None,
        enableWebAudio: type === 'video' ? false : true,
      }
      await meetingManager.join(meetingSessionConfiguration, options)
      await meetingManager.start()
    }

    startMeeting(meetingInfo, attendeeInfo)
  }, [attendeeInfo, meetingInfo, meetingManager, type])

  // handle video stream
  useEffect(() => {
    if (!audioVideo || type !== 'video') return

    const observer: AudioVideoObserver = {
      videoTileDidUpdate: (tileState): void => {
        setStream(tileState.boundVideoStream)
      },
      videoTileWasRemoved: () => {
        setStream(null)
      },
    }

    audioVideo.addObserver(observer)
    return () => {
      audioVideo.removeObserver(observer)
    }
  }, [audioVideo, type])

  // handle audio stream
  useEffect(() => {
    if (!audioVideo || type !== 'audio') return

    const audioMixObserver: AudioMixObserver = {
      meetingAudioStreamBecameActive: (activeStream: MediaStream) => {
        if (!isStreamer) {
          // Ensure AudioContext is running (may still be suspended if mount
          // happened outside a user-gesture call-stack)
          if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume().then(() => {
              console.warn(`🔊 CHIME AudioContext resumed in audioMixObserver — state: ${audioContextRef.current.state}`)
            })
          } else {
            console.warn(`🔊 CHIME audioMixObserver fired — AudioContext state: ${audioContextRef.current.state}`)
          }

          const destination = createAudioProcessingChain(audioContextRef.current, 'raw', activeStream)

          const source = audioContextRef.current.createMediaStreamSource(destination.stream)
          source.connect(gainNodeRef.current)
          gainNodeRef.current.connect(audioContextRef.current.destination)

          setStream(destination.stream)
        } else {
          setStream(activeStream)
        }
      },
      meetingAudioStreamBecameInactive: (inactiveStream: MediaStream) => {
        if (!isStreamer) setStream(null)
      },
    }

    audioVideo.addAudioMixObserver(audioMixObserver)
    // audioVideo.addDeviceChangeObserver(deviceChangeObserver)
    // audioVideo.addObserver(audioVideoObserver)
    if (!isStreamer) {
      audioVideo.realtimeMuteLocalAudio()
    }
    return () => {
      audioVideo.removeAudioMixObserver(audioMixObserver)
      // audioVideo.removeDeviceChangeObserver(deviceChangeObserver)
      // audioVideo.removeObserver(audioVideoObserver)
    }
  }, [audioVideo, isStreamer, type])

  // start video input device
  useEffect(() => {
    if (!selectedDeviceId || type !== 'video') return

    meetingManager.startVideoInputDevice(selectedDeviceId).then(() => {
      // Create normalized device list for easier searching
      const normalizedDevices = [
        ...medicalCameras.map((camera) => ({
          deviceId: camera.device.deviceId,
          label: camera.device.label,
          type: 'medicalCamera' as const,
          originalDevice: camera,
        })),
        ...videoMediaDevices.map((device) => ({
          deviceId: device.deviceId,
          label: device.label,
          type: 'standardCamera' as const,
          originalDevice: device,
        })),
      ]

      const selectedDevice = normalizedDevices.find((device) => device.deviceId === selectedDeviceId)

      console.warn(`📹 CHIME VIDEO DEVICE ACTIVATED:`, {
        requestedDeviceId: selectedDeviceId,
        requestedDeviceName: selectedDevice?.label || 'Unknown',
        deviceType: selectedDevice?.type || 'unknown',
      })

      audioVideo?.startLocalVideoTile()
      setIsVideoEnabled(true)
    })
  }, [audioVideo, meetingManager, selectedDeviceId, setIsVideoEnabled, type, medicalCameras, videoMediaDevices])

  // start audio input device
  useEffect(() => {
    if (!isStreamer || !selectedDeviceId || type !== 'audio') return

    getUserMedia(microphonePluginConstraintsGenerator(selectedDeviceId), (error, initialStream) => {
      if (error || !initialStream) return
      ;(async () => {
        const { workingStream, requestedLabel, actualTrackLabel, mismatch } = await selectDeviceWithRetry(selectedDeviceId, initialStream, 'Chime')

        if (mismatch) {
          enqueueSnackbar(
            t('component.ui.deviceSelectionDialog.deviceMismatch', {
              requested: requestedLabel || selectedDeviceId,
              actual: actualTrackLabel,
              defaultValue: 'Could not start "{{requested}}". Using "{{actual}}" instead.',
            }),
            { variant: 'warning', autoHideDuration: 8000 }
          )
        }

        // Send raw working stream to Chime as input
        await meetingManager.startAudioInputDevice(workingStream)

        // Ensure AudioContext is running before building the monitoring chain
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume()
          console.warn(`🔊 CHIME AudioContext resumed in audio input setup — state: ${audioContextRef.current.state}`)
        }

        // Local monitoring uses raw->destination (no filters)
        const destination = createAudioProcessingChain(audioContextRef.current, 'raw', workingStream)

        const source = audioContextRef.current.createMediaStreamSource(destination.stream)
        source.connect(gainNodeRef.current)
        gainNodeRef.current.connect(audioContextRef.current.destination)

        console.warn(`🎵 CHIME AUDIO DEVICE ACTIVATED:`, {
          requestedDeviceId: selectedDeviceId,
          requestedDeviceName: requestedLabel || 'Unknown',
          actualTrackLabel,
          actualTrackId: workingStream.getAudioTracks()[0]?.id || 'No track ID',
          deviceMismatch: mismatch,
        })

        setStream(destination.stream)
      })()
    })
  }, [audioVideo, isStreamer, meetingManager, selectedDeviceId, setStream, type])

  useEffect(() => {
    onDevicesChange({ medicalCameras, stethoscopes, audioInputMediaDevices, videoMediaDevices })
  }, [activeDiagnosticRTCService, audioInputMediaDevices, medicalCameras, onDevicesChange, stethoscopes, videoMediaDevices])

  useEffect(() => {
    onStreamChange(stream)
  }, [onStreamChange, stream])

  useEffect(() => {
    return () => {
      meetingManager.leave()
    }
  }, [meetingManager])

  return <>{children}</>
}

export default forwardRef(Chime)

function VideoRenderer({ isStreamer }: { isStreamer: boolean }) {
  const videoGridRef = useRef<HTMLDivElement>(null)

  // Enhanced video fitting fallback for cross-browser compatibility
  useEffect(() => {
    const videoGrid = videoGridRef.current
    if (!videoGrid) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const videoElements = entry.target.querySelectorAll<HTMLVideoElement>('video.ch-video')

      for (const video of videoElements) {
        // Ensure video has proper sizing
        if (video.style.objectFit !== 'contain') {
          video.style.objectFit = 'contain'
        }
        if (!video.style.width || video.style.width === '0px') {
          video.style.width = '100%'
        }
        if (!video.style.height || video.style.height === '0px') {
          video.style.height = '100%'
        }

        // Ensure parent container is properly sized
        const parent = video.parentElement
        if (parent && (!parent.style.width || parent.style.width === '0px')) {
          parent.style.width = '100%'
          parent.style.height = '100%'
          parent.style.position = 'relative'
          parent.style.overflow = 'hidden'
        }
      }
    })
    observer.observe(videoGrid)

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <Box ref={videoGridRef} height="100%" width="100%">
      <VideoGrid layout="featured" size={1}>
        <ContentShare />
        {isStreamer ? <StyledLocalVideo /> : <StyledRemoteVideos />}
      </VideoGrid>
    </Box>
  )
}
